import React, { useState } from 'react';
import ResourceCard from './ResourceCard';

const VPCContainer = ({ vpcData, filters, searchQuery }) => {
  const [isExpanded, setIsExpanded] = useState(true);

  const filterResources = (resources) => {
    if (!resources) return [];
    
    return resources.filter(resource => {
      // Apply search filter
      if (searchQuery) {
        const query = searchQuery.toLowerCase();
        const matchesSearch = 
          resource.name?.toLowerCase().includes(query) ||
          resource.id?.toLowerCase().includes(query) ||
          resource.type?.toLowerCase().includes(query);
        
        if (!matchesSearch) return false;
      }

      // Apply type filters
      const resourceType = resource.type?.toLowerCase();
      if (resourceType?.includes('ec2') && !filters.showCompute) return false;
      if (resourceType?.includes('rds') && !filters.showDatabase) return false;
      if (resourceType?.includes('s3') && !filters.showStorage) return false;
      if ((resourceType?.includes('alb') || resourceType?.includes('gateway')) && !filters.showNetworking) return false;

      return true;
    });
  };

  const getResourcesBySubnet = (subnetId) => {
    const allResources = [
      ...(vpcData.ec2Instances || []),
      ...(vpcData.rdsInstances || []),
      ...(vpcData.loadBalancers || [])
    ];
    
    return filterResources(
      allResources.filter(resource => resource.subnetId === subnetId)
    );
  };

  if (!isExpanded) {
    return (
      <div 
        className="bg-gradient-to-r from-blue-50 to-blue-100 border-4 border-blue-400 rounded-xl p-6 mb-4 opacity-75 hover:opacity-100 transition-opacity cursor-pointer"
        onClick={() => setIsExpanded(true)}
      >
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img 
              src="/aws-icons/Networking-Content-Delivery/Virtual-Private-Cloud.svg" 
              alt="VPC"
              className="w-10 h-10"
              onError={(e) => {
                e.target.style.display = 'none';
              }}
            />
            <div>
              <div className="font-bold text-lg text-gray-900">{vpcData.name || 'VPC'}</div>
              <div className="text-sm text-gray-700 font-mono mt-1">{vpcData.cidr}</div>
            </div>
          </div>
          <div className="flex items-center gap-4">
            <div className="text-sm text-gray-600">
              <span>{Object.keys(vpcData.subnets || {}).length} subnets</span>
            </div>
            <button className="text-blue-600 hover:text-blue-700 text-sm font-medium">
              Expand
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-gradient-to-br from-gray-50 to-blue-50 border-4 border-blue-500 rounded-xl shadow-lg p-6 mb-6">
      {/* VPC Header */}
      <div className="flex items-start justify-between mb-6">
        <div className="flex items-center gap-3">
          <img 
            src="/aws-icons/Networking-Content-Delivery/Virtual-Private-Cloud.svg" 
            alt="VPC"
            className="w-12 h-12"
            onError={(e) => {
              e.target.style.display = 'none';
            }}
          />
          <div>
            <div className="font-bold text-xl text-gray-900">{vpcData.name || 'VPC'}</div>
            <div className="text-sm text-gray-700 font-mono mt-1">{vpcData.cidr}</div>
            <div className="flex items-center gap-4 mt-2">
              <span className="text-xs text-gray-600 font-mono">{vpcData.id}</span>
              {vpcData.tags?.Environment && (
                <span className={`px-2 py-0.5 text-xs font-medium rounded ${
                  vpcData.tags.Environment.toLowerCase() === 'production' 
                    ? 'bg-blue-100 text-blue-800'
                    : vpcData.tags.Environment.toLowerCase() === 'development'
                    ? 'bg-yellow-100 text-yellow-800'
                    : 'bg-green-100 text-green-800'
                }`}>
                  {vpcData.tags.Environment}
                </span>
              )}
            </div>
          </div>
        </div>
        <div className="text-right text-sm text-gray-600">
          <button
            onClick={() => setIsExpanded(false)}
            className="text-gray-500 hover:text-gray-700 text-sm"
          >
            Collapse
          </button>
        </div>
      </div>

      {/* Subnets */}
      {Object.entries(vpcData.subnets || {}).map(([subnetId, subnetData]) => {
        const subnetResources = getResourcesBySubnet(subnetId);
        const isPublic = subnetData.public || subnetData.type === 'public';

        return (
          <div 
            key={subnetId}
            className={`${
              isPublic 
                ? 'bg-blue-50 border-blue-400' 
                : 'bg-green-50 border-green-400'
            } border-2 rounded-lg p-4 mb-4`}
          >
            {/* Subnet Header */}
            <div className="flex items-center justify-between mb-3">
              <div>
                <div className="font-semibold text-sm text-gray-900">
                  {subnetData.name || 'Subnet'}
                </div>
                <div className="text-xs text-gray-600 font-mono">{subnetData.cidr}</div>
                <div className="text-xs text-gray-500 mt-1">
                  AZ: {subnetData.availabilityZone || 'N/A'}
                </div>
              </div>
              <span className={`px-2 py-1 text-xs font-medium rounded ${
                isPublic 
                  ? 'bg-blue-100 text-blue-700' 
                  : 'bg-green-100 text-green-700'
              }`}>
                {isPublic ? 'Public' : 'Private'}
              </span>
            </div>

            {/* Resources in Subnet */}
            {subnetResources.length > 0 ? (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                {subnetResources.map((resource, idx) => (
                  <ResourceCard key={`${resource.id}-${idx}`} resource={resource} />
                ))}
              </div>
            ) : (
              <div className="text-center py-4 text-sm text-gray-500">
                No resources in this subnet
              </div>
            )}
          </div>
        );
      })}

      {/* Internet Gateways */}
      {vpcData.internetGateways && vpcData.internetGateways.length > 0 && (
        <div className="flex items-center gap-3 pt-4 border-t border-blue-200 mt-4">
          <div className="w-8 h-8 bg-blue-100 rounded flex items-center justify-center">
            <span className="text-xs font-bold text-blue-700">IGW</span>
          </div>
          <div>
            <div className="font-semibold text-sm text-gray-900">Internet Gateway</div>
            {vpcData.internetGateways.map((igw, idx) => (
              <div key={idx} className="text-xs text-gray-500 font-mono">
                {igw.id || igw}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* NAT Gateways */}
      {vpcData.natGateways && vpcData.natGateways.length > 0 && (
        <div className="flex items-center gap-3 pt-4 border-t border-blue-200 mt-4">
          <div className="w-8 h-8 bg-green-100 rounded flex items-center justify-center">
            <span className="text-xs font-bold text-green-700">NAT</span>
          </div>
          <div>
            <div className="font-semibold text-sm text-gray-900">NAT Gateway</div>
            {vpcData.natGateways.map((nat, idx) => (
              <div key={idx} className="text-xs text-gray-500 font-mono">
                {nat.id || nat}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default VPCContainer;
